import React, { useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";

import { IgDivider } from "@intelligems/ig-theme";
import { Grid, List } from "@mui/material";
import { fromGqlId } from "@src/api/formatters/shopify";
import { useCollections } from "@src/graphql/hooks/useCollections";
import { useProductTags } from "@src/graphql/hooks/useProductTags";
import { useProductTypes } from "@src/graphql/hooks/useProductTypes";
import { useProductVendors } from "@src/graphql/hooks/useProductVendors";
import { useDispatch, useSelector } from "@src/store";
import {
  getProductPickerFilters,
  setProductPickerFilter,
} from "@src/store/slices/productPicker";

import { ProductFilterItem } from "./ProductFilterItem";

interface FilterProps {
  selected: string | undefined;
  onToggleSelected: (name: string) => void;
  allowIgProducts?: boolean;
}

export const ProductFilterList = ({
  allowIgProducts,
}: {
  allowIgProducts?: boolean;
}) => {
  const [selected, setSelected] = useState<string>();

  const onToggleSelected = (name: string) => {
    setSelected((selected) => {
      return selected !== name ? name : undefined;
    });
  };

  return (
    <Grid
      item
      container
      direction={"column"}
      rowSpacing={2}
      sx={{ height: "365px" }}
    >
      <Grid item>
        <ProductTypeFilter
          selected={selected}
          onToggleSelected={onToggleSelected}
        />
      </Grid>
      <Grid item>
        <VendorFilter selected={selected} onToggleSelected={onToggleSelected} />
      </Grid>
      <Grid item>
        <TagFilter
          selected={selected}
          allowIgProducts={allowIgProducts}
          onToggleSelected={onToggleSelected}
        />
      </Grid>
      <Grid item>
        <StatusFilter selected={selected} onToggleSelected={onToggleSelected} />
      </Grid>
      <Grid item>
        <CollectionFilter
          selected={selected}
          onToggleSelected={onToggleSelected}
        />
      </Grid>
    </Grid>
  );
};

const ProductTypeFilter = ({ selected, onToggleSelected }: FilterProps) => {
  const dispatch = useDispatch();
  const data = useProductTypes();
  const filters = useSelector(getProductPickerFilters);
  const edges = data?.productTypes?.edges ?? [];

  const items = useMemo(
    () => [
      ...edges.map((node) => ({
        label: node.node,
        value: node.node,
      })),
    ],
    [edges]
  );
  const onChange = (value: string[]) =>
    void dispatch(
      setProductPickerFilter({
        filter: "product_type",
        value,
      })
    );

  return (
    <ProductFilterItem
      name={"Product Type"}
      selected={selected}
      onToggleSelected={onToggleSelected}
      items={items}
      onChange={onChange}
      selectedValues={filters.product_type ?? ""}
    />
  );
};

const VendorFilter = ({ selected, onToggleSelected }: FilterProps) => {
  const dispatch = useDispatch();
  const data = useProductVendors();
  const filters = useSelector(getProductPickerFilters);
  const edges = data?.productVendors?.edges ?? [];

  const items = useMemo(
    () => [
      ...edges.map((node) => ({
        label: node.node,
        value: node.node,
      })),
    ],
    [edges]
  );
  const onChange = (value: string[]) =>
    void dispatch(
      setProductPickerFilter({
        filter: "vendor",
        value,
      })
    );

  return (
    <ProductFilterItem
      name={"Vendor"}
      selected={selected}
      onToggleSelected={onToggleSelected}
      items={items}
      onChange={onChange}
      selectedValues={filters.vendor ?? ""}
    />
  );
};

const TagFilter = ({
  selected,
  onToggleSelected,
  allowIgProducts,
}: FilterProps) => {
  const dispatch = useDispatch();
  const data = useProductTags();
  const filters = useSelector(getProductPickerFilters);
  const edges = data?.productTags?.edges ?? [];

  const items = useMemo(() => {
    const tags: { label: string; value: string }[] = [];
    for (const edge of edges) {
      const tag = edge.node;
      if (allowIgProducts) {
        tags.push({
          label: tag,
          value: tag,
        });
      } else if (
        !tag.startsWith("dup_") &&
        !tag.startsWith("ig_") &&
        tag !== "price_test"
      ) {
        tags.push({
          label: tag,
          value: tag,
        });
      }
    }
    return tags;
  }, [edges]);
  const onChange = (value: string[]) =>
    void dispatch(
      setProductPickerFilter({
        filter: "tag",
        value,
      })
    );

  return (
    <ProductFilterItem
      name={"Tags"}
      selected={selected}
      onToggleSelected={onToggleSelected}
      items={items}
      onChange={onChange}
      disabled={filters.collection_id?.length > 0}
      selectedValues={filters.tag ?? ""}
    />
  );
};

const StatusFilter = ({ selected, onToggleSelected }: FilterProps) => {
  const dispatch = useDispatch();
  const filters = useSelector(getProductPickerFilters);

  const items = useMemo(
    () => [
      { label: "Active", value: "active" },
      { label: "Draft", value: "draft" },
      { label: "Archived", value: "archived" },
    ],
    []
  );
  const onChange = (value: string[]) =>
    void dispatch(
      setProductPickerFilter({
        filter: "status",
        value,
      })
    );

  return (
    <ProductFilterItem
      name={"Status"}
      selected={selected}
      onToggleSelected={onToggleSelected}
      items={items}
      onChange={onChange}
      selectedValues={filters.status ?? ""}
    />
  );
};

const CollectionFilter = ({ selected, onToggleSelected }: FilterProps) => {
  const dispatch = useDispatch();
  const filters = useSelector(getProductPickerFilters);
  const data = useCollections();

  const edges = data?.collections?.edges ?? [];

  const items = useMemo(() => {
    const collections: { label: string; value: string }[] = [];
    for (const edge of edges) {
      const collection = edge.node;
      collections.push({
        label: collection.title,
        value: fromGqlId(collection.id),
      });
    }
    return collections;
  }, [edges]);

  const onChange = (value: string[]) => {
    void dispatch(
      setProductPickerFilter({
        filter: "collection_id",
        value,
      })
    );
  };

  return (
    <ProductFilterItem
      name={"Collections"}
      selected={selected}
      disabled={filters.tag?.length > 0}
      onToggleSelected={onToggleSelected}
      items={items}
      onChange={onChange}
      selectedValues={filters.collection_id ?? ""}
    />
  );
};
